import React from 'react';

import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Select from 'react-select';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import { isEmpty } from 'lodash';

import styles from './ACTicketsDialog.module.scss';

const MAX_FILE_SIZE = 2 * 1024 * 1024;

/** Quill reports empty content as markup (`<p><br></p>`), never as an empty string. */
const isQuillEmpty = (value) => {
    if (!value) return true;
    return value.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim() === '';
};

const ACCEPTED_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/svg+xml', 'image/webp'];

export default class ACTicketsDialog extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            data: {
                notes: '',
                files: [],
                existingFiles : [],
                removedExistingFiles : [],
                // Venue-local HH:mm the ticket is booked for. Customer-facing, like the note:
                // it shows on the occasion's itinerary once it's ready.
                time: '',
                // Only used when the dialog assigns tickets in place of a route.
                provider: null,
                price: '',
            },
            isDragging: false,
            fileError: null,
        };
        this.onClickUpdate = this.onClickUpdate.bind(this);
        this.handleProviderChange = this.handleProviderChange.bind(this);
        this.handlePriceChange = this.handlePriceChange.bind(this);
        this.handleTimeChange = this.handleTimeChange.bind(this);
        this.handleFileSelect = this.handleFileSelect.bind(this);
        this.handleDrop = this.handleDrop.bind(this);
        this.handleDragOver = this.handleDragOver.bind(this);
        this.handleDragEnter = this.handleDragEnter.bind(this);
        this.handleDragLeave = this.handleDragLeave.bind(this);
        this.handleNotesChange = this.handleNotesChange.bind(this);
        this.removeFile = this.removeFile.bind(this);
    }

    // Sync from props only as the dialog opens. componentWillReceiveProps fires on every
    // parent re-render, so seeding state here unconditionally threw away whatever was
    // being typed the moment anything above re-rendered (a provider search, a planner
    // refresh) — which is how notes were reaching the API as undefined.
    componentWillReceiveProps(nextProps) {
        const isOpening = nextProps.open && !this.props.open;
        if (!isOpening) {
            return;
        }

        const { ticket, stop } = nextProps;
        this.setState({
            data: {
                notes: ticket?.info || '',
                // Start each session clean, or a file picked for one booking would be
                // submitted again the next time the dialog is opened.
                files: [],
                existingFiles: ticket?.documents || [],
                removedExistingFiles: [],
                // Read off `ticket`, not `stop` — it is the only prop both entry points pass.
                time: ticket?.time || '',
                // A ticket route already assigned carries its provider/price on the stop.
                provider: stop?.provider
                    ? { ...stop.provider, id: stop.providerId ?? stop.provider.id }
                    : null,
                price: stop?.item?.price ?? '',
            },
            fileError: null,
        });
    }

    handleProviderChange(selectedOption) {
        this.setState(({ data }) => ({
            data: { ...data, provider: selectedOption?.provider ?? null },
        }));
    }

    handlePriceChange(event) {
        const { value } = event.target;
        this.setState(({ data }) => ({
            data: { ...data, price: value },
        }));
    }

    handleTimeChange(event) {
        const { value } = event.target;
        this.setState(({ data }) => ({
            data: { ...data, time: value },
        }));
    }

    /**
     * What time the ticket is booked for. Stored and sent as venue-local `HH:mm` — a 7:30pm
     * session is 7:30pm at the venue, so there is no timezone to carry. A native time input
     * gives exactly that string back, which is why this isn't a MUI date picker.
     */
    renderTicketTime() {
        const { isPreview } = this.props;
        const { data } = this.state;

        return (
            <div className={styles.uploadSection}>
                <span className={styles.fieldLabel}>Booked time (optional)</span>
                <TextField
                    type="time"
                    inputProps={{ step: 900 }}
                    InputLabelProps={{ shrink: true }}
                    disabled={isPreview === true}
                    fullWidth
                    value={data.time}
                    onChange={this.handleTimeChange}
                />
            </div>
        );
    }

    /**
     * What the component's ticket-required slot says: who to buy from, where, and the
     * internal instructions for doing it. Read-only — it is content, set upstream in the hub.
     */
    renderTicketRequirement() {
        const { ticketRequirement } = this.props;
        if (!ticketRequirement) return null;

        return (
            <div className={styles.ticketRequirement}>
                <span className={styles.fieldLabel}>Ticket required</span>
                <div>
                    <b>Provider:</b> {ticketRequirement.providerName}
                </div>
                {ticketRequirement.bookingUrl && (
                    <div>
                        <b>Book at:</b>{' '}
                        <a href={ticketRequirement.bookingUrl} target="_blank" rel="noopener noreferrer">
                            {ticketRequirement.bookingUrl}
                        </a>
                    </div>
                )}
                {ticketRequirement.instructions && (
                    <div className={styles.ticketRequirementInstructions}>
                        {ticketRequirement.instructions}
                    </div>
                )}
            </div>
        );
    }

    /**
     * Preview replaces the drop zone with the tickets themselves — images inline, PDFs in
     * an embedded viewer — so ops can see what the customer gets. `documents` come from
     * the API with short-lived presigned URLs; stored documents hold only S3 keys.
     */
    renderTicketPreview() {
        const documents = (this.props.documents || []).filter((doc) => doc?.url);

        if (!documents.length) {
            return (
                <div className={styles.uploadSection}>
                    <span className={styles.fieldLabel}>Ticket</span>
                    <div className={styles.dropZone}>
                        <div className={styles.dropZoneContent}>
                            <span>No ticket files attached</span>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className={styles.uploadSection}>
                <span className={styles.fieldLabel}>Ticket</span>
                {documents.map((doc, index) => (
                    <div key={`${doc.name}-${index}`} className={styles.ticketPreview}>
                        {doc.type === 'application/pdf' ? (
                            <iframe
                                title={doc.name || `Ticket ${index + 1}`}
                                src={doc.url}
                                className={styles.ticketPreviewFrame}
                            />
                        ) : (
                            <img
                                alt={doc.name || `Ticket ${index + 1}`}
                                src={doc.url}
                                className={styles.ticketPreviewImage}
                            />
                        )}
                        <a href={doc.url} target="_blank" rel="noopener noreferrer">
                            {doc.name || `Ticket ${index + 1}`}
                        </a>
                    </div>
                ))}
            </div>
        );
    }

    // Provider and price only apply when tickets stand in for a route — the stop the
    // backend generates carries them so the cost lands in unit economics.
    renderProviderAndPrice() {
        const { showProviderAndPrice, providers = [] } = this.props;
        if (!showProviderAndPrice) return null;

        const { data } = this.state;
        const options = providers.map((provider) => ({
            label: provider.name,
            value: provider.id,
            provider,
        }));
        const selected = data.provider
            ? { label: data.provider.name, value: data.provider.id, provider: data.provider }
            : null;

        return (
            <div className={styles.uploadSection}>
                <span className={styles.fieldLabel}>Provider (optional)</span>
                <Select
                    options={options}
                    value={selected}
                    onChange={this.handleProviderChange}
                    onInputChange={this.props.onProviderSearchFunc}
                    placeholder="Search providers…"
                    isClearable
                />
                <TextField
                    label="Ticket cost (optional)"
                    type="number"
                    value={data.price}
                    onChange={this.handlePriceChange}
                    fullWidth
                />
            </div>
        );
    }

    validateFile(file) {
        if (!file) {
            return null;
        }
        if (file.size > MAX_FILE_SIZE) {
            return 'File must be 2MB or smaller.';
        }
        if (!ACCEPTED_TYPES.includes(file.type)) {
            return 'Only PDF and image files are supported.';
        }
        return null;
    }

    addFiles(fileList) {
        const files = Array.from(fileList || []);
        if (files.length === 0) {
            return;
        }

        const validFiles = [];
        const errors = [];

        files.forEach(file => {
            const error = this.validateFile(file);
            if (error) {
                errors.push(`${file.name}: ${error}`);
            } else {
                validFiles.push(file);
            }
        });

        this.setState(({ data }) => ({
            data: {
                ...data,
                files: [...data.files, ...validFiles],
            },
            fileError: errors.length ? errors.join(' ') : null,
        }));
    }

    handleFileSelect(event) {
        this.addFiles(event.target.files);
    }

    handleDrop(event) {
        event.preventDefault();
        this.addFiles(event.dataTransfer.files);
        this.setState({ isDragging: false });
    }

    handleDragOver(event) {
        event.preventDefault();
    }

    handleDragEnter(event) {
        event.preventDefault();
        this.setState({ isDragging: true });
    }

    handleDragLeave() {
        this.setState({ isDragging: false });
    }

    removeFile(index) {
        this.setState(({ data }) => ({
            data: {
                ...data,
                files: data.files.filter((file, fileIndex) => fileIndex !== index),
            },
        }));
    }

    removeExistingFiles(index) {
        this.setState(({ data }) => ({
            data: {
                ...data,
                existingFiles: data.existingFiles.filter((file, fileIndex) => fileIndex !== index),
                removedExistingFiles: [...data.removedExistingFiles, data.existingFiles[index]],
            },
        }));
    }

    handleNotesChange(notes) {
        this.setState(({ data }) => ({
            data: {
                ...data,
                notes,
            },
        }));
    }

    onClickUpdate() {
        const { data } = this.state;
        const { areNotesCompulsory, onClickUpdateFunc } = this.props;

        // Quill leaves markup behind for empty content, which would otherwise be stored
        // and rendered to the customer as a blank paragraph.
        const notes = isQuillEmpty(data.notes) ? '' : data.notes;

        if (areNotesCompulsory === true && isEmpty(notes)) {
            alert('Please enter notes');
            return;
        }
        if (data.files.some(file => this.validateFile(file))) {
            alert('Please remove invalid files or select valid files under 2MB.');
            return;
        }
        onClickUpdateFunc({ ...data, notes });
    }

    CloseDialog() {
        this.props.onClickCancelFunc();
    }
render() {
    const { title, isPreview } = this.props; //TODO: Use prop-types
    const { data, isDragging, fileError } = this.state;
    return (
      <Dialog
        open={this.props.open}
        aria-labelledby="form-dialog-title"
        fullWidth
        onEscapeKeyDown={this.props.onClickCancelFunc}
      >
        <DialogTitle id="form-dialog-title">
          <strong>{title}</strong>
        </DialogTitle>
        <DialogContent
            style={{
                height: '500px',
            }}
        >
            {this.renderTicketRequirement()}
            {isPreview ? this.renderTicketPreview() : (
            <div className={styles.uploadSection}>
                <span className={styles.fieldLabel}>Upload Ticket File (PDF, JPG, PNG)</span>
                <div
                    className={`${styles.dropZone} ${isDragging ? styles.dropZoneActive : ''}`}
                    onDrop={this.handleDrop}
                    onDragOver={this.handleDragOver}
                    onDragEnter={this.handleDragEnter}
                    onDragLeave={this.handleDragLeave}
                >
                    <input
                        type="file"
                        multiple
                        accept="application/pdf,image/*"
                        className={styles.hiddenInput}
                        onChange={this.handleFileSelect}
                    />
                    <div className={styles.dropZoneContent}>
                        <div>
                            <CloudUploadIcon style={{fontSize : 48}} />
                        </div>
                        <span>Click to browse or drag files here</span>
                        <span className={styles.dropZoneHint}>Accepted: PDF, images. Max size 2MB per file.</span>
                    </div>
                </div>
                {data?.files.length > 0 && (
                  <div className={styles.fileList}>
                    {data.files.map((file, index) => (
                      <div key={`${file.name}-${index}`} className={styles.fileItem}>
                        <span>{file.name}</span>
                        <button
                          type="button"
                          onClick={() => this.removeFile(index)}
                          className={styles.removeFileButton}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {fileError && (
                  <div className={styles.errorText}>{fileError}</div>
                )}
                {data.existingFiles.length > 0 && (
                  <div className={styles.fileList}>
                    {data.existingFiles.map((file, index) => (
                      <div key={`${file.name}-${index}`} className={styles.fileItem}>
                        <span>{file.name}</span>
                        <button
                          type="button"
                          onClick={() => this.removeExistingFiles(index)}
                          className={styles.removeFileButton}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
            </div>
            )}
            {this.renderTicketTime()}
            {!isPreview && this.renderProviderAndPrice()}
            <div className={styles.noteSection}>
                {/* The customer reads this with the ticket in Phoenix, so it is copy for them -
                    where to collect it, what to show at the door - not a note for ops. */}
                <span className={styles.fieldLabel}>Notes (shown to the customer)</span>
                <div className={styles.noteSection}>
                    <ReactQuill
                        theme="snow"
                        placeholder={isPreview ? '' : 'Start writing...'}
                        style={{ height: '100%' }}
                        value={data.notes}
                        onChange={this.handleNotesChange}
                        readOnly={isPreview}
                        modules={isPreview ? { toolbar: false } : undefined}
                    />
                </div>
            </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={this.props.onClickCancelFunc} color="secondary">
            {isPreview ? 'Close' : 'Cancel'}
          </Button>
          {!isPreview && (
            <Button
              onClick={this.onClickUpdate}
              variant="contained"
              color="primary"
              style={{
                color: 'white',
              }}
            >
              Save Ticket Info
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  }
}
